import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabase';
import type { CalendarEvent as EventType, EventCategory, DateRange, CategoryFilter } from '../types';
import { useSavedEvents } from '../contexts/SavedEventsContext';
import { dateShortcutToRange } from '../utils/dateShortcuts';
import { TOWNS } from '../constants/towns';
import { EventList } from '../components/EventList';
import { EventFilters } from '../components/EventFilters';
import { CalendarMonthView } from '../components/CalendarMonthView';
import { EventDetailModal } from '../components/EventDetailModal';
import './Calendar.css';

const PAGE_SIZE = 24;
type ViewMode = 'month' | 'week' | 'day' | 'list';

function firstDayOfMonth(y: number, m: number) {
  return `${y}-${String(m).padStart(2, '0')}-01`;
}
function lastDayOfMonth(y: number, m: number) {
  const last = new Date(y, m, 0);
  return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}-${String(last.getDate()).padStart(2, '0')}`;
}

function toIsoDate(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function startOfWeek(d: Date) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function addDays(d: Date, days: number) {
  const date = new Date(d);
  date.setDate(date.getDate() + days);
  return date;
}

function isEventOnDay(event: EventType, day: Date) {
  const ds = toIsoDate(day);
  return event.start_date <= ds && event.end_date >= ds;
}

function CalendarWeekView({
  anchorDate,
  events,
  onEventClick,
}: {
  anchorDate: Date;
  events: EventType[];
  onEventClick: (event: EventType) => void;
}) {
  const weekStart = startOfWeek(anchorDate);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  return (
    <section className="calendar-week-grid" aria-label="Week view">
      {weekDays.map((day) => {
        const dayEvents = events.filter((event) => isEventOnDay(event, day));
        return (
          <article key={toIsoDate(day)} className="calendar-week-day">
            <header className="calendar-week-day-head">
              <p>{day.toLocaleDateString('en-AU', { weekday: 'short' })}</p>
              <strong>{day.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}</strong>
            </header>
            <div className="calendar-week-events">
              {dayEvents.length === 0 ? (
                <p className="calendar-empty-note">No events</p>
              ) : (
                dayEvents.map((event) => (
                  <button
                    key={`${event.id}-${toIsoDate(day)}`}
                    type="button"
                    className={`calendar-week-event calendar-week-event--${event.category?.slug ?? ''}`}
                    onClick={() => onEventClick(event)}
                  >
                    <span>{event.title}</span>
                    <small>{event.location ?? 'Regional'}</small>
                  </button>
                ))
              )}
            </div>
          </article>
        );
      })}
    </section>
  );
}

function CalendarDayView({
  anchorDate,
  events,
  onEventClick,
}: {
  anchorDate: Date;
  events: EventType[];
  onEventClick: (event: EventType) => void;
}) {
  const dayEvents = events.filter((event) => isEventOnDay(event, anchorDate));
  return (
    <section className="calendar-day-view" aria-label="Day view">
      <header className="calendar-day-head">
        <h3>{anchorDate.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</h3>
        <p>{dayEvents.length} {dayEvents.length === 1 ? 'event' : 'events'}</p>
      </header>
      {dayEvents.length === 0 ? (
        <p className="calendar-empty-note">No events for this date.</p>
      ) : (
        <div className="calendar-day-list">
          {dayEvents.map((event) => (
            <button
              key={event.id}
              type="button"
              className={`calendar-day-card calendar-day-card--${event.category?.slug ?? ''}`}
              onClick={() => onEventClick(event)}
            >
              <div>
                <h4>{event.title}</h4>
                <p>{event.description ?? 'No description provided.'}</p>
              </div>
              <aside>
                <span>{event.location ?? 'Regional'}</span>
                <small>
                  {new Date(event.start_date).toLocaleDateString('en-AU')} -{' '}
                  {new Date(event.end_date).toLocaleDateString('en-AU')}
                </small>
              </aside>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

export default function Calendar() {
  const [searchParams, setSearchParams] = useSearchParams();
  const savedOnly = searchParams.get('saved') === '1';
  const { savedIds, isSaved, toggleSaved } = useSavedEvents();

  const [events, setEvents] = useState<EventType[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<EventType[]>([]);
  const [categories, setCategories] = useState<EventCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>(null);
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>(null);
  const [searchQuery, setSearchQuery] = useState<string>(() => searchParams.get('q') ?? '');
  const [townFilter, setTownFilter] = useState<string | null>(() => searchParams.get('town') || null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [view, setView] = useState<ViewMode>(() => (savedOnly ? 'list' : 'month'));
  const [selectedEvent, setSelectedEvent] = useState<EventType | null>(null);

  useEffect(() => {
    const date = searchParams.get('date');
    if (date === 'weekend' || date === 'month' || date === 'nextmonth') {
      setDateRange(dateShortcutToRange(date));
    }
    const cat = searchParams.get('category');
    if (cat) setCategoryFilter(cat);
    const town = searchParams.get('town');
    if (town) setTownFilter(town);
  }, []);

  useEffect(() => {
    const eventId = searchParams.get('e');
    if (!eventId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('events')
        .select('*, category:event_categories(*), host:stakeholders(*)')
        .eq('id', eventId)
        .eq('status', 'published')
        .single();
      if (!cancelled && data) setSelectedEvent(data as EventType);
    })();
    return () => { cancelled = true; };
  }, [searchParams]);

  const now = new Date();
  const [calendarAnchor, setCalendarAnchor] = useState<Date>(new Date(now.getFullYear(), now.getMonth(), now.getDate()));

  // Categories change rarely — don't re-fetch on every visibility refresh (saves 1 query per tab return).
  useEffect(() => {
    async function loadCategories() {
      const { data, error: e } = await supabase
        .from('event_categories')
        .select('*')
        .order('name');
      if (e) setError(e.message);
      else setCategories((data as EventCategory[]) ?? []);
    }
    loadCategories();
  }, []);

  const fetchListPage = useCallback(
    async (pageNum: number) => {
      const from = (pageNum - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      let q = supabase
        .from('events')
        .select('*, category:event_categories(*), host:stakeholders(*)')
        .eq('status', 'published')
        .order('start_date', { ascending: true })
        .range(from, to);
      if (dateRange?.from) q = q.gte('end_date', dateRange.from);
      if (dateRange?.to) q = q.lte('start_date', dateRange.to);
      if (categoryFilter) q = q.eq('category_id', categoryFilter);
      const qTrim = searchQuery.trim();
      if (qTrim) q = q.ilike('title', `%${qTrim}%`);
      const town = TOWNS.find((t) => t.slug === townFilter);
      if (town && town.slug !== 'regional') {
        q = q.ilike('location', `%${town.name}%`);
      }
      const { data, error: e } = await q;
      if (e) {
        setError(e.message);
        return [];
      }
      const list = (data as EventType[]) ?? [];
      setHasMore(list.length === PAGE_SIZE);
      return list;
    },
    [dateRange, categoryFilter, searchQuery, townFilter]
  );

  useEffect(() => {
    if (savedOnly) {
      if (savedIds.length === 0) {
        setEvents([]);
        setLoading(false);
        setHasMore(false);
        return;
      }
      let cancelled = false;
      setLoading(true);
      (async () => {
        const { data, error: e } = await supabase
          .from('events')
          .select('*, category:event_categories(*), host:stakeholders(*)')
          .eq('status', 'published')
          .in('id', savedIds)
          .order('start_date', { ascending: true });
        if (!cancelled) {
          if (e) setError(e.message);
          else setEvents((data as EventType[]) ?? []);
          setLoading(false);
          setHasMore(false);
        }
      })();
      return () => { cancelled = true; };
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPage(1);
    (async () => {
      const list = await fetchListPage(1);
      if (!cancelled) {
        setEvents(list);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [savedOnly, savedIds, dateRange, categoryFilter, fetchListPage]);

  const loadMore = useCallback(async () => {
    if (savedOnly) return;
    const nextPage = page + 1;
    setLoadingMore(true);
    const list = await fetchListPage(nextPage);
    setEvents((prev) => [...prev, ...list]);
    setPage(nextPage);
    setLoadingMore(false);
  }, [savedOnly, page, fetchListPage]);

  useEffect(() => {
    if (view === 'list') return;
    let cancelled = false;
    setCalendarLoading(true);
    let from = '';
    let to = '';
    if (view === 'month') {
      const y = calendarAnchor.getFullYear();
      const m = calendarAnchor.getMonth() + 1;
      from = firstDayOfMonth(y, m);
      to = lastDayOfMonth(y, m);
    } else if (view === 'week') {
      const weekStart = startOfWeek(calendarAnchor);
      from = toIsoDate(weekStart);
      to = toIsoDate(addDays(weekStart, 6));
    } else {
      from = toIsoDate(calendarAnchor);
      to = toIsoDate(calendarAnchor);
    }
    (async () => {
      let q = supabase
        .from('events')
        .select('*, category:event_categories(*), host:stakeholders(*)')
        .eq('status', 'published')
        .gte('end_date', from)
        .lte('start_date', to)
        .order('start_date', { ascending: true });
      if (categoryFilter) q = q.eq('category_id', categoryFilter);
      const qTrim = searchQuery.trim();
      if (qTrim) q = q.ilike('title', `%${qTrim}%`);
      const town = TOWNS.find((t) => t.slug === townFilter);
      if (town && town.slug !== 'regional') q = q.ilike('location', `%${town.name}%`);
      const { data, error: e } = await q;
      if (!cancelled) {
        if (e) setError(e.message);
        else setCalendarEvents((data as EventType[]) ?? []);
        setCalendarLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [view, calendarAnchor, categoryFilter, searchQuery, townFilter]);

  const hasActiveFilters = Boolean(dateRange?.from || dateRange?.to || categoryFilter);

  const goPrevPeriod = () => {
    const d = new Date(calendarAnchor);
    if (view === 'month') d.setMonth(d.getMonth() - 1);
    else d.setDate(d.getDate() - (view === 'week' ? 7 : 1));
    setCalendarAnchor(d);
  };
  const goNextPeriod = () => {
    const d = new Date(calendarAnchor);
    if (view === 'month') d.setMonth(d.getMonth() + 1);
    else d.setDate(d.getDate() + (view === 'week' ? 7 : 1));
    setCalendarAnchor(d);
  };
  const goToday = () => setCalendarAnchor(new Date());

  const calendarLabel = (() => {
    if (view === 'month') {
      return calendarAnchor.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
    }
    if (view === 'week') {
      const start = startOfWeek(calendarAnchor);
      const end = addDays(start, 6);
      return `${start.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
    return calendarAnchor.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  })();

  return (
    <div className="events-page">
      <section className="events-hero">
        <div className="events-hero-image" aria-hidden="true" />
        <div className="events-hero-overlay" aria-hidden="true" />
        <div className="events-hero-content">
          <p className="events-eyebrow">Regional Events Calendar</p>
          <h1>Advertise, discover and plan around regional events.</h1>
          <p>
            Browse the calendar, filter by town and category, then submit your event so local
            businesses and stakeholders can plan ahead.
          </p>
          <div className="events-hero-actions">
            <Link to="/submit" className="events-hero-btn events-hero-btn-primary">
              Submit your event
            </Link>
            <Link to="/about" className="events-hero-btn events-hero-btn-secondary">
              How this works
            </Link>
          </div>
        </div>
      </section>
      <div className="events-toolbar">
        <EventFilters
          categories={categories}
          dateRange={dateRange}
          categoryFilter={categoryFilter}
          onDateRangeChange={setDateRange}
          onCategoryChange={setCategoryFilter}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          townFilter={townFilter}
          onTownChange={setTownFilter}
        />
        <div className="events-view-tabs" aria-label="View mode">
          <button
            type="button"
            className={view === 'month' ? 'is-active' : ''}
            onClick={() => setView('month')}
          >
            Month
          </button>
          <button
            type="button"
            className={view === 'week' ? 'is-active' : ''}
            onClick={() => setView('week')}
          >
            Week
          </button>
          <button
            type="button"
            className={view === 'day' ? 'is-active' : ''}
            onClick={() => setView('day')}
          >
            Day
          </button>
          <button
            type="button"
            className={view === 'list' ? 'is-active' : ''}
            onClick={() => setView('list')}
          >
            List
          </button>
        </div>
      </div>

      {error && (
        <div className="events-error" role="alert">
          {error}
        </div>
      )}

      {view === 'list' && (
        savedOnly ? (
          loading ? (
            <div className="events-loading">Loading saved events…</div>
          ) : (
            <EventList
              events={events}
              hasActiveFilters={false}
              onOpenDetail={(e) => setSelectedEvent(e)}
              isSaved={isSaved}
              emptyTitle="No saved events"
              emptyDesc="Save events from the list to see them here."
            />
          )
        ) : loading ? (
          <div className="events-loading">Loading events…</div>
        ) : (
          <EventList
            events={events}
            hasMore={hasMore}
            onLoadMore={loadMore}
            loadingMore={loadingMore}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={() => {
              setDateRange(null);
              setCategoryFilter(null);
            }}
            onOpenDetail={(e) => setSelectedEvent(e)}
            isSaved={isSaved}
          />
        )
      )}

      {view !== 'list' && (
        calendarLoading ? (
          <div className="events-loading">Loading calendar…</div>
        ) : (
          <section className="calendar-shell">
            <header className="calendar-shell-header">
              <div>
                <p className="events-count">
                  {calendarEvents.length} {calendarEvents.length === 1 ? 'event' : 'events'} in view
                </p>
                <h2>{calendarLabel}</h2>
              </div>
              <div className="calendar-shell-actions">
                <button type="button" onClick={goPrevPeriod} aria-label="Previous period">←</button>
                <button type="button" onClick={goToday}>Today</button>
                <button type="button" onClick={goNextPeriod} aria-label="Next period">→</button>
              </div>
            </header>
            {view === 'month' ? (
              <CalendarMonthView
                events={calendarEvents}
                year={calendarAnchor.getFullYear()}
                month={calendarAnchor.getMonth() + 1}
                onPrevMonth={goPrevPeriod}
                onNextMonth={goNextPeriod}
                onEventClick={(e) => setSelectedEvent(e)}
                onDayClick={(date) => {
                  setCalendarAnchor(date);
                  setView('day');
                }}
              />
            ) : view === 'week' ? (
              <CalendarWeekView
                anchorDate={calendarAnchor}
                events={calendarEvents}
                onEventClick={(e) => setSelectedEvent(e)}
              />
            ) : (
              <CalendarDayView
                anchorDate={calendarAnchor}
                events={calendarEvents}
                onEventClick={(e) => setSelectedEvent(e)}
              />
            )}
          </section>
        )
      )}

      {selectedEvent && (
        <EventDetailModal
          event={selectedEvent}
          isSaved={isSaved(selectedEvent.id)}
          onToggleSaved={() => toggleSaved(selectedEvent.id)}
          onClose={() => {
            setSelectedEvent(null);
            const e = searchParams.get('e');
            if (e) {
              searchParams.delete('e');
              setSearchParams(searchParams, { replace: true });
            }
          }}
        />
      )}
    </div>
  );
}
